import { useCallback, useRef, useState } from 'react';
import { Button, DataGrid, FormDialog, unwrapAction, useGridConfig } from '@eo/shared';
import { Plus } from 'lucide-react';

import type { ClientItem } from './api/types';
import { makeClientFormConfig } from './config/formConfig';
import { clientMatchFields, clientToValues, clientValuesToPatch } from './helpers';
import { UNASSIGNED_OWNER } from './constants';
import type { ClientFormValues } from './types';
import { makeClientsGridConfig } from './config/gridConfig';
import type { ClientsTabProps } from './types';

export function ClientsTab({
  clients,
  staff,
  options,
  onQueryOptions,
  pagination,
  onCreate,
  onUpdate,
}: ClientsTabProps) {
  const [editing, setEditing] = useState<ClientItem | null>(null);
  const [creating, setCreating] = useState(false);

  // `useGridConfig` memoizes on first render, so the lookup it captures has to
  // keep a stable identity while the staff list behind it changes.
  const staffRef = useRef(staff);
  staffRef.current = staff;
  const ownerName = useCallback(
    (id: string | null) =>
      id ? (staffRef.current.find((m) => m.id === id)?.name ?? 'Unknown') : 'Unassigned',
    [],
  );

  const config = useGridConfig(makeClientsGridConfig, { onEdit: setEditing, ownerName });

  return (
    <section className="flex flex-col gap-4">
      <DataGrid<ClientItem>
        config={config}
        rows={clients}
        pagination={pagination}
        headerActions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            Add client
          </Button>
        }
      />

      <FormDialog<ClientFormValues>
        open={creating}
        onOpenChange={setCreating}
        title="Add client"
        config={makeClientFormConfig({ staff, options, onQueryOptions })}
        initial={{
          name: '',
          ownerUserId: UNASSIGNED_OWNER,
          isActive: true,
          aliases: '',
          domains: '',
        }}
        validate={(values) => (values.name.trim() ? {} : { name: 'Enter a client name.' })}
        onSubmit={async (values) => {
          unwrapAction(
            await onCreate({
              name: values.name.trim(),
              ownerUserId: values.ownerUserId === UNASSIGNED_OWNER ? null : values.ownerUserId,
              ...clientMatchFields(values),
            }),
            'Adding the client failed. Try again.',
          );
        }}
        submitLabel="Add client"
        pendingLabel="Adding…"
        successToast={{ title: 'Client added.' }}
      />

      {editing && (
        <FormDialog<ClientFormValues>
          open
          onOpenChange={(open) => !open && setEditing(null)}
          title={`Edit ${editing.name}`}
          config={makeClientFormConfig({ staff, options, onQueryOptions })}
          initial={clientToValues(editing)}
          validate={(values) => (values.name.trim() ? {} : { name: 'Enter a client name.' })}
          onSubmit={async (values) => {
            unwrapAction(
              await onUpdate({ id: editing.id, patch: clientValuesToPatch(values) }),
              'Saving the client failed. Try again.',
            );
            setEditing(null);
          }}
          submitLabel="Save changes"
          pendingLabel="Saving…"
          successToast={{ title: 'Client updated.' }}
        />
      )}
    </section>
  );
}
